import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, CheckCircle, Clock, X, ShieldCheck, RefreshCw, Upload, Zap, AlertCircle } from 'lucide-react';
import { api } from '../../../lib/api';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { useTranslation } from 'react-i18next';

interface LabUploadModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  onUploadComplete: (files: any[]) => void;
}

interface ExtractionProgress {
  [docId: string]: {
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    extractedVitals: boolean;
    extractedLabs: boolean;
    extractedMedications: boolean;
    error?: string;
  };
}

/**
 * LabUploadModal
 * Displays a QR code for secure mobile upload of lab documents.
 * Listens via WebSocket for real-time notification from the backend when upload is done.
 */
export function LabUploadModal({ open, onClose, patientId, onUploadComplete }: LabUploadModalProps) {
  const { t } = useTranslation('prescriptions');
  const [session, setSession] = useState<{ token: string; sessionId: string; expiresAt: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { status: wsStatus, subscribe } = useWebSocket();
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [previews, setPreviews] = useState<{ [key: string]: string }>({});
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [directUploading, setDirectUploading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress>({});

  // Initialize session when modal opens
  useEffect(() => {
    if (open) {
      createSession();
    }
  }, [open]);

  // Handle countdown timer
  useEffect(() => {
    if (!session?.expiresAt || uploadedFiles.length > 0) {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      const difference = new Date(session.expiresAt).getTime() - new Date().getTime();
      return Math.max(0, Math.floor(difference / 1000));
    };

    const initialTime = calculateTimeLeft();
    setTimeLeft(initialTime);

    if (initialTime <= 0) return;

    const timer = setInterval(() => {
      const time = calculateTimeLeft();
      setTimeLeft(time);
      if (time <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [session, uploadedFiles]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (wsStatus === 'connected' && open) {
      console.log('📡 Subscribing to lab upload notifications...');
      const unsub = subscribe('/user/queue/notifications', (message: any) => {
        const data = typeof message === 'string' ? JSON.parse(message) : message;
        if (data.type === 'LAB_UPLOAD_COMPLETE') {
          console.log('✅ Received lab upload event:', data);

          const incomingFiles = data.files || [];

          setUploadedFiles(prev => {
            // Merge files avoiding duplicates
            const existingPaths = new Set(prev.map(f => f.filePath));
            const newOnes = incomingFiles.filter((f: any) => !existingPaths.has(f.filePath));
            return [...prev, ...newOnes];
          });

          // Initialize extraction progress for new files
          incomingFiles.forEach((f: any) => {
            if (!extractionProgress[f.id]) {
              setExtractionProgress(prev => ({
                ...prev,
                [f.id]: {
                  status: 'pending',
                  progress: 0,
                  extractedVitals: false,
                  extractedLabs: false,
                  extractedMedications: false,
                },
              }));
            }
          });

          if (incomingFiles.length > 0) {
            incomingFiles.forEach((f: any) => fetchPreview(f.filePath));
          }
        }
      });
      return unsub;
    }
  }, [wsStatus, open, subscribe, extractionProgress]);

  // Poll for extraction progress
  useEffect(() => {
    if (uploadedFiles.length === 0) return;

    const pollInterval = setInterval(async () => {
      uploadedFiles.forEach(async (file) => {
        if (extractionProgress[file.id]?.status === 'completed' || extractionProgress[file.id]?.status === 'failed') {
          return; // Skip if already done
        }

        try {
          // You would normally fetch extraction status from an endpoint
          // For now, we'll update based on file processingStatus if available
          if (file.processingStatus === 'PROCESSING') {
            setExtractionProgress(prev => ({
              ...prev,
              [file.id]: {
                ...prev[file.id],
                status: 'processing',
                progress: 50,
              },
            }));
          } else if (file.processingStatus === 'COMPLETED') {
            setExtractionProgress(prev => ({
              ...prev,
              [file.id]: {
                ...prev[file.id],
                status: 'completed',
                progress: 100,
                extractedVitals: !!file.extractedVitals && Object.keys(file.extractedVitals).length > 0,
                extractedLabs: !!file.extractedLabs && Object.keys(file.extractedLabs).length > 0,
                extractedMedications: !!file.extractedMedications && Object.keys(file.extractedMedications).length > 0,
              },
            }));
          } else if (file.processingStatus === 'FAILED') {
            setExtractionProgress(prev => ({
              ...prev,
              [file.id]: {
                ...prev[file.id],
                status: 'failed',
                progress: 0,
                error: file.verificationError || 'Extraction failed',
              },
            }));
          }
        } catch (err) {
          console.error('Error polling extraction status:', err);
        }
      });
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [uploadedFiles, extractionProgress]);

  const fetchPreview = async (filePath: string) => {
    try {
      const response = await api.labUpload.getPreviewUrl(filePath);
      if (response.data?.url) {
        setPreviews(prev => ({ ...prev, [filePath]: response.data?.url || '' }));
      }
    } catch (err) {
      console.error('Failed to fetch preview URL:', err);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Allowed formats: PDF, Images
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const invalidFile = files.find(f => !allowedTypes.includes(f.type));
    
    if (invalidFile) {
      setError(`File ${invalidFile.name} has an unsupported format. Please upload PDF or Images only.`);
      return;
    }

    // Increased limit to match backend (20MB client-side check, 25MB backend)
    const largeFile = files.find(f => f.size > 20 * 1024 * 1024);
    if (largeFile) {
      setError(`File ${largeFile.name} is too large. Maximum allowed size is 20MB.`);
      return;
    }

    setDirectUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('file', f));

      const response = await api.labUpload.directUpload(patientId, files, consent);
      if (response.data && response.data.files) {
        setUploadedFiles(response.data.files);

        // Initialize extraction progress for directly uploaded files
        response.data.files.forEach((f: any) => {
          setExtractionProgress(prev => ({
            ...prev,
            [f.id]: {
              status: 'pending',
              progress: 0,
              extractedVitals: false,
              extractedLabs: false,
              extractedMedications: false,
            },
          }));
          fetchPreview(f.filePath);
        });
      } else {
        // Use backend safe message if available
        setError(response.error || 'Upload failed. The file might be corrupted or too large.');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Upload failed due to a network or server error.';
      setError(message);
    } finally {
      setDirectUploading(false);
    }
  };

  const createSession = async () => {
    setLoading(true);
    setError(null);
    setUploadedFiles([]);
    setPreviews({});
    setExtractionProgress({});
    try {
      // Pass consent given status to backend
      const response = await api.labUpload.createSession(patientId, consent);
      if (response.data) {
        setSession({
          ...response.data,
          sessionId: response.data.sessionId || ''
        });
      } else {
        setError(response.error || 'Failed to create secure session');
      }
    } catch (err) {
      setError('Connection to security bridge failed');
    } finally {
      setLoading(false);
    }
  };

  // Generate the full URL for the QR code.
  // When running on localhost, the phone can't resolve "localhost" to this PC.
  // Vite is configured with host:true so it's reachable via the LAN IP — we just need
  // to build the URL with that IP instead.  We derive it from VITE_API_BASE_URL:
  // if the developer set it to http://192.168.1.10:8080, we use 192.168.1.10 as the
  // frontend hostname too (same machine, port 5173).
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  let qrOrigin = window.location.origin;
  if (isLocalhost) {
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
      const apiHost = apiBase ? new URL(apiBase).hostname : '';
      if (apiHost && apiHost !== 'localhost' && apiHost !== '127.0.0.1') {
        qrOrigin = `${window.location.protocol}//${apiHost}:${window.location.port}`;
      }
    } catch {
      // fallback to window.location.origin — QR will show localhost warning
    }
  }
  const uploadUrl = session
    ? `${qrOrigin}/mobile-upload?token=${encodeURIComponent(session.token)}&expiresAt=${encodeURIComponent(session.expiresAt)}`
    : '';
  const qrUsesLocalhost = uploadUrl.includes('localhost') || uploadUrl.includes('127.0.0.1');

  const handleClose = async () => {
    // Kill the session bridge immediately when modal is closed
    if (session?.sessionId) {
      try {
        await api.labUpload.invalidateSession(session.sessionId);
      } catch (err) {
        console.warn('Failed to invalidate session on close:', err);
      }
    }
    onClose();
  };

  const handleReview = async () => {
    if (session?.sessionId) {
      try {
        // Mark session as USED so it shows up in history
        await api.labUpload.finalizeSession(session.sessionId);
      } catch (err) {
        console.warn('Failed to finalize session:', err);
      }
    }
    onUploadComplete(uploadedFiles);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box sx={{ bgcolor: 'primary.main', p: 0.5, borderRadius: 1, display: 'flex' }}>
            <Smartphone size={18} color="white" />
          </Box>
          <Typography variant="h6" fontWeight={700}>{t('mobileLabUpload')}</Typography>
        </Stack>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        {isLocalhost && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              {t('deviceNetworkingWarning')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {t('yourPhoneCannotReachLocalhost')} 
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {t('accessThisEmrUsingYourComputers')}
            </Typography>
          </Alert>
        )}
        {loading || directUploading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              {directUploading ? 'Uploading file to secure storage...' : 'Establishing secure clinical bridge...'}
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={createSession} startIcon={<RefreshCw size={14} />}>
                {t('retry')}
              </Button>
            }>
              {error}
            </Alert>
          </Box>
        ) : uploadedFiles.length > 0 ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Box sx={{ mb: 2, display: 'inline-flex', bgcolor: 'success.light', p: 1, borderRadius: '50%' }}>
              <CheckCircle size={32} color="#2e7d32" />
            </Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {uploadedFiles.length} Document{uploadedFiles.length > 1 ? 's' : ''} Received!
            </Typography>

            <Box sx={{ mt: 2, mb: 2, maxHeight: 450, overflowY: 'auto', p: 1 }}>
              {uploadedFiles.map((file, index) => {
                const progress = extractionProgress[file.id];
                const isExtracting =progress?.status === 'processing';
                const isCompleted = progress?.status === 'completed';
                const isFailed = progress?.status === 'failed';

                return (
                  <Box key={index} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Box sx={{ p: 1, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '75%', flex: 1 }}>
                        <Typography variant="caption" fontWeight={600} noWrap>
                          {file.fileName}
                        </Typography>
                        {file.patientNameVerified ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'success.50', color: 'success.dark', px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'success.100' }}>
                            <ShieldCheck size={10} />
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800 }}>{t('aiVerified')}</Typography>
                          </Box>
                        ) : file.patientNameVerified === false && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'error.50', color: 'error.main', px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'error.100' }}>
                            <X size={10} />
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800 }}>MISMATCH</Typography>
                          </Box>
                        )}
                      </Box>
                      {file.fileName.toLowerCase().endsWith('.pdf') && (
                        <Typography variant="caption" color="error.main" fontWeight={800}>PDF</Typography>
                      )}
                    </Box>

                    {/* Extraction Progress Section */}
                    {isExtracting && (
                      <Box sx={{ px: 2, py: 1.5, bgcolor: '#e3f2fd', borderBottom: '1px solid', borderColor: '#bbdefb' }}>
                        <Box display="flex" gap={1} alignItems="center" mb={1}>
                          <Zap size={16} style={{ color: '#1976d2' }} />
                          <Typography variant="caption" fontWeight={600} color="primary">
                            {t('extractingClinicalData')}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Chip
                            icon={progress?.extractedVitals ? <CheckCircle size={14} /> : undefined}
                            label={t('vitals')}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: progress?.extractedVitals ? '#4caf50' : '#bdbdbd',
                              color: progress?.extractedVitals ? '#4caf50' : '#9e9e9e',
                            }}
                          />
                          <Chip
                            icon={progress?.extractedLabs ? <CheckCircle size={14} /> : undefined}
                            label={t('labs')}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: progress?.extractedLabs ? '#4caf50' : '#bdbdbd',
                              color: progress?.extractedLabs ? '#4caf50' : '#9e9e9e',
                            }}
                          />
                          <Chip
                            icon={progress?.extractedMedications ? <CheckCircle size={14} /> : undefined}
                            label={t('medications')}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: progress?.extractedMedications ? '#4caf50' : '#bdbdbd',
                              color: progress?.extractedMedications ? '#4caf50' : '#9e9e9e',
                            }}
                          />
                        </Box>
                        <LinearProgress variant="determinate" value={progress?.progress || 0} sx={{ mt: 1 }} />
                      </Box>
                    )}

                    {/* Extraction Complete Section */}
                    {isCompleted && (
                      <Box sx={{ px: 2, py: 1, bgcolor: '#f1f8e9', borderBottom: '1px solid', borderColor: '#dcedc8' }}>
                        <Box display="flex" gap={1} alignItems="center">
                          <CheckCircle size={16} style={{ color: '#4caf50' }} />
                          <Typography variant="caption" fontWeight={600} color="success.dark">
                            {t('extractionComplete')}
                          </Typography>
                          {progress?.extractedVitals || progress?.extractedLabs || progress?.extractedMedications ? (
                            <Typography variant="caption" color="textSecondary">
                              ({[progress?.extractedVitals && 'vitals', progress?.extractedLabs && 'labs', progress?.extractedMedications && 'meds'].filter(Boolean).join(', ')})
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    )}

                    {/* Extraction Error Section */}
                    {isFailed && (
                      <Box sx={{ px: 2, py: 1, bgcolor: '#ffebee', borderBottom: '1px solid', borderColor: '#ffcdd2' }}>
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <AlertCircle size={16} style={{ color: '#f44336', marginTop: 2 }} />
                          <Box flex={1}>
                            <Typography variant="caption" fontWeight={600} color="error">
                              {t('extractionFailed')}
                            </Typography>
                            {progress?.error && (
                              <Typography variant="caption" color="error" display="block">
                                {progress.error}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {file.verificationError && !isExtracting && (
                      <Box sx={{ px: 2, py: 0.5, bgcolor: 'error.50', borderBottom: '1px solid', borderColor: 'error.100' }}>
                        <Typography variant="caption" color="error.main" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          <strong>{t('alert')}</strong> {file.verificationError}
                        </Typography>
                      </Box>
                    )}

                    {previews[file.filePath] ? (
                      file.fileName.toLowerCase().endsWith('.pdf') ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Button
                            component="a"
                            href={previews[file.filePath]}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                          >
                            {t('viewPdf')}
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          component="img"
                          src={previews[file.filePath]}
                          alt={`Preview ${index}`}
                          sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
                        />
                      )
                    ) : (
                      <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">{t('loadingPreview')}</Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Alert severity="success" icon={<ShieldCheck size={18} />} sx={{ textAlign: 'left', mt: 2 }}>
              {t('documentsEncryptedAtRestClinicalData')}
            </Alert>
          </Box>
        ) : session ? (
          <Box sx={{ py: 1, textAlign: 'center' }}>
            <Box sx={{ mb: 2, textAlign: 'left', p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    size="small" 
                    checked={consent} 
                    onChange={(e) => setConsent(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {t('confirmPatientConsentForStorageAnd')}
                  </Typography>
                }
                sx={{ mb: 0.5, display: 'flex' }}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    size="small" 
                    checked={verified} 
                    onChange={(e) => setVerified(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {t('documentsBelongToTheCurrentPatient')}
                  </Typography>
                }
                sx={{ display: 'flex' }}
              />
            </Box>

            {!consent || !verified ? (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2, py: 0 }}>
                <Typography variant="caption" fontWeight={600}>
                  {t('pleaseConfirmConsentAndOwnershipTo')}
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', px: 1 }}>
                {t('scanThisQrWithASmartphone')}
              </Typography>
            )}
            
            {qrUsesLocalhost && consent && verified && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left', fontSize: '0.75rem' }}>
                {t('qrLinksTo')} <strong>localhost</strong> {t('phoneCantReachThisPcVia')} <code>VITE_API_BASE_URL</code> {t('toYourLanIpEG')} <code>http://192.168.1.10:8080</code>{t('andRestartTheDevServerOr')}
              </Alert>
            )}

            <Box
              sx={{
                bgcolor: 'white',
                p: 3,
                display: 'inline-block',
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                opacity: (timeLeft <= 0 || !consent || !verified) ? 0.3 : 1,
                pointerEvents: (!consent || !verified) ? 'none' : 'auto'
              }}
            >
              <QRCodeSVG value={uploadUrl || 'no-session'} size={220} />
              {timeLeft <= 0 && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: 4
                }}>
                  <Button variant="contained" size="small" onClick={createSession} startIcon={<RefreshCw size={14} />}>
                    {t('refreshQr')}
                  </Button>
                </Box>
              )}
            </Box>

            <Stack direction="row" justifyContent="center" alignItems="center" gap={1} sx={{ mt: 3, color: timeLeft <= 0 ? 'error.main' : 'text.secondary' }}>
              <Clock size={14} />
              <Typography variant="caption" fontWeight={500}>
                {timeLeft > 0 ? (
                  `Session expires in ${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`
                ) : (
                  'Session expired'
                )}
              </Typography>
              {timeLeft > 0 && (
                <IconButton size="small" onClick={createSession} sx={{ ml: 0.5 }}>
                  <RefreshCw size={12} />
                </IconButton>
              )}
            </Stack>

            <Box sx={{ mt: 3, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, bgcolor: (consent && verified) ? 'action.hover' : 'grey.100', opacity: (consent && verified) ? 1 : 0.6 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                {t('orUploadFromComputer')}
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<Upload size={16} />}
                fullWidth
                size="small"
                disabled={!consent || !verified}
                sx={{ bgcolor: (consent && verified) ? 'background.paper' : 'transparent' }}
              >
                {t('selectFiles')}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
              <Typography variant="caption" color="primary.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                <ShieldCheck size={14} /> {t('hipaaCompliantSecureTransfer')}
              </Typography>
              <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.5, lineHeight: 1.2 }}>
                {t('theMobileDeviceWillNotStore')}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {t('close')}
        </Button>
        {uploadedFiles.length > 0 && (
          <Button 
            variant="contained" 
            color="success" 
            disableElevation
            onClick={handleReview}
            sx={{ fontWeight: 700, px: 3 }}
          >
            Review {uploadedFiles.length} Document{uploadedFiles.length > 1 ? 's' : ''}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
